import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, storage } from '../shared/storage/firebase';
import { useAuth } from '../domains/auth/contexts/AuthContext';
import { getCertification, listTeacherLearningProgress, saveStepProgress } from '../domains/resources/services/learningCenterService';
import {
  fallbackCertificationById,
  ONBOARDING_CERTIFICATION_ID,
  TEACHER_CERTIFICATION_ID,
} from '../domains/resources/fallbackCertifications';
import type { Certification, CertificationStep, LearningFile } from '../domains/resources/types';
import './TeacherOnboardingPage.css';

function fn(name: string) {
  return httpsCallable(getFunctions(), name);
}

type Task = {
  id: string;
  title: string;
  description: string;
  requiresContract: boolean;
  completion: string;
  linkUrl?: string | null;
  linkLabel?: string | null;
  uploadField?: string;
  status: string;
  writtenContent?: string;
  video?: LearningFile | null;
  documents?: LearningFile[];
  order?: number;
  published?: boolean;
  required?: boolean;
  signedName?: string | null;
  signatureUrl?: string | null;
};

function withCheckrProgress(tasks: Task[], progress: Array<{ stepId?: string; status?: string }>): Task[] {
  const checkrStatus = progress.find(entry => entry.stepId === 'backgroundCheck')?.status;
  if (checkrStatus !== 'pending_review' && checkrStatus !== 'completed') return tasks;
  return tasks.map(task => task.id === 'backgroundCheck' && task.status !== 'completed'
    ? { ...task, status: checkrStatus }
    : task);
}

const STAFF_HANDBOOK_PDF = '/docs/Overture_Teacher_Handbook_2026_v2.pdf';

const ONBOARDING_TASK_IDS = new Set([
  'slack', 'profile', 'handbook', 'backgroundCheck', 'schoolBackgroundCheck',
  'mandatedReporter', 'payroll', 'merch',
]);

const QUICK_ONBOARDING_TASKS: Array<Omit<Task, 'status'>> = [
  { id: 'contract', title: 'Employment contract', description: 'Review and sign your employment agreement.', requiresContract: false, completion: 'contract_sign' },
  { id: 'slack', title: 'Communication (Slack)', description: 'Look for a Slack invitation email and set up your account.', requiresContract: true, completion: 'teacher_ack' },
  { id: 'profile', title: 'Overture web-app profile', description: 'Complete your profile setup and availability.', requiresContract: true, completion: 'teacher_ack' },
  { id: 'handbook', title: 'Staff handbook', description: 'Read the Overture Staff Manual, then sign that you have read and will follow it.', requiresContract: true, completion: 'handbook_sign', linkUrl: STAFF_HANDBOOK_PDF, linkLabel: 'Staff handbook PDF' },
  { id: 'backgroundCheck', title: 'Checkr background check', description: 'Complete Checkr from your email invitation, then request a review so Overture can mark this complete.', requiresContract: true, completion: 'admin_review' },
  { id: 'schoolBackgroundCheck', title: 'Massachusetts CORI authorization form', description: 'Provide the information and authorization Overture needs to run your Massachusetts CORI check.', requiresContract: true, completion: 'state_background_form', required: false },
  { id: 'mandatedReporter', title: 'Mandated reporter compliance', description: 'Complete the required regional course and upload your certificate.', requiresContract: true, completion: 'upload', uploadField: 'mandatoryReportingCertificationFile' },
  { id: 'payroll', title: 'Payroll (Gusto)', description: 'Use your Gusto invitation to set up payroll.', requiresContract: true, completion: 'teacher_ack' },
  { id: 'merch', title: 'Overture gear', description: 'Provide your shirt size.', requiresContract: true, completion: 'teacher_ack' },
];

export default function TeacherOnboardingPage() {
  const { currentUser, userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [certification, setCertification] = useState<Certification | null>(
    fallbackCertificationById(ONBOARDING_CERTIFICATION_ID)
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [signMode, setSignMode] = useState<'draw' | 'type' | 'upload'>('draw');
  const [typedName, setTypedName] = useState('');
  const [handbookAttested, setHandbookAttested] = useState(false);
  const [handbookSignedName, setHandbookSignedName] = useState('');
  const [handbookSignMode, setHandbookSignMode] = useState<'draw' | 'type'>('draw');
  const [shirtSize, setShirtSize] = useState('');
  const [coriForm, setCoriForm] = useState({
    firstName: '', middleInitial: '', lastName: '', suffix: '', formerLastNames: [''],
    dateOfBirth: '', placeOfBirth: '', lastSixSsn: '', noSocialSecurityNumber: false,
    sex: '', height: '', eyeColor: '', race: '', governmentIdNumber: '', governmentIdState: '',
    fatherFullName: '', motherFullName: '', streetAddress: '', apartment: '', city: '', state: 'MA', zip: '',
    preferredEmail: '', phone: '', signedName: '', attested: false,
  });
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const handbookCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!currentUser?.uid) throw new Error('Sign in required.');
      const [teacherSnap, userSnap, managedCertification, learningProgress] = await Promise.all([
        getDoc(doc(db, 'teachers', currentUser.uid)),
        getDoc(doc(db, 'users', currentUser.uid)),
        getCertification(ONBOARDING_CERTIFICATION_ID).catch(() => null),
        listTeacherLearningProgress(currentUser.uid).catch(() => []),
      ]);
      setCertification(managedCertification || fallbackCertificationById(ONBOARDING_CERTIFICATION_ID));
      const teacher = teacherSnap.data() as any;
      const user = userSnap.data() as any;
      const hiring = teacher?.hiring || {};
      const tasks = QUICK_ONBOARDING_TASKS.map(task => ({
        ...task,
        status: hiring?.onboarding?.[task.id]?.status || 'not_started',
        completedAt: hiring?.onboarding?.[task.id]?.completedAt || null,
        signedName: hiring?.onboarding?.[task.id]?.signedName || null,
        signatureUrl: hiring?.onboarding?.[task.id]?.signatureUrl || null,
      }));
      const quickData = {
        displayName: user?.displayName || userData?.displayName || '',
        email: user?.email || '',
        staffStatus: user?.staffStatus || 'applicant',
        hiringStatus: hiring?.status || user?.hiringStatus || null,
        offerAcceptedAt: hiring?.offerAcceptedAt || null,
        contractSignedAt: hiring?.contractSignedAt || null,
        onboardingDueAt: hiring?.onboardingDueAt || null,
        offerPdfUrl: hiring?.offerPdf?.url || null,
        contractUnsignedPdfUrl: hiring?.contractUnsignedPdf?.url || null,
        contractSignedPdfUrl: hiring?.contractSignedPdf?.url || null,
        shirtSize: hiring?.shirtSize || user?.shirtSize || '',
        metro: hiring?.metro || hiring?.locationLabel || user?.state || '',
        tasks: withCheckrProgress(tasks, learningProgress),
      };
      let resolvedData = quickData;
      try {
        const canonical: any = await fn('hiringGetOnboarding')({});
        const canonicalData = canonical.data || quickData;
        resolvedData = {
          ...canonicalData,
          tasks: withCheckrProgress(canonicalData.tasks || quickData.tasks, learningProgress),
        };
      } catch {
        // The direct Firestore data above keeps onboarding usable if Functions is unavailable.
      }
      setData(resolvedData);
      setShirtSize(String(resolvedData.shirtSize || ''));
      setTypedName(String(resolvedData.displayName || ''));
      setHandbookSignedName(String(resolvedData.displayName || ''));
      const names = String(resolvedData.displayName || '').trim().split(/\s+/);
      setCoriForm(current => ({
        ...current,
        firstName: current.firstName || names[0] || '',
        lastName: current.lastName || names.slice(1).join(' '),
        preferredEmail: current.preferredEmail || String(resolvedData.email || ''),
        signedName: current.signedName || String(resolvedData.displayName || ''),
      }));
    } catch (e: any) {
      try {
        const [res, managedCertification]: any[] = await Promise.all([
          fn('hiringGetOnboarding')({}),
          getCertification(ONBOARDING_CERTIFICATION_ID).catch(() => null),
        ]);
        setData(res.data);
        setCertification(managedCertification || fallbackCertificationById(ONBOARDING_CERTIFICATION_ID));
        setShirtSize(String(res.data?.shirtSize || ''));
        setTypedName(String(res.data?.displayName || userData?.displayName || ''));
        setHandbookSignedName(String(res.data?.displayName || userData?.displayName || ''));
      } catch (fallbackError: any) {
        setError(fallbackError?.message || e?.message || 'Failed to load onboarding.');
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, userData?.displayName]);

  useEffect(() => {
    load();
  }, [load]);

  const paintCanvas = (canvas: HTMLCanvasElement | null) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  };

  useEffect(() => {
    paintCanvas(canvasRef.current);
  }, [data?.contractSignedAt, signMode]);

  useEffect(() => {
    paintCanvas(handbookCanvasRef.current);
  }, [handbookSignMode, data?.contractSignedAt]);

  const pointer = (canvas: HTMLCanvasElement | null, e: React.PointerEvent<HTMLCanvasElement>, action: 'start' | 'move' | 'end') => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvas.height;
    if (action === 'start') {
      drawing.current = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else if (action === 'move' && drawing.current) {
      ctx.lineTo(x, y);
      ctx.stroke();
    } else {
      drawing.current = false;
    }
  };

  const clearCanvas = (canvas: HTMLCanvasElement | null) => {
    paintCanvas(canvas);
  };

  const acceptOwnOffer = async () => {
    setBusy('offer');
    setError(null);
    try {
      await fn('hiringAcceptOwnOffer')({});
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to accept offer.');
    } finally {
      setBusy(null);
    }
  };

  const signContract = async (uploadedFile?: File | null) => {
    setBusy('contract');
    setError(null);
    try {
      if (signMode === 'upload') {
        if (!uploadedFile) throw new Error('Choose a signed PDF to upload.');
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = () => reject(new Error('Failed to read file.'));
          reader.readAsDataURL(uploadedFile);
        });
        await fn('hiringSignContract')({
          mode: 'upload',
          uploadedPdfBase64: dataUrl,
          uploadedFilename: uploadedFile.name,
        });
      } else if (signMode === 'type') {
        await fn('hiringSignContract')({ mode: 'type', typedName: typedName || userData?.displayName });
      } else {
        const canvas = canvasRef.current;
        if (!canvas) throw new Error('Signature pad missing.');
        const dataUrl = canvas.toDataURL('image/png');
        await fn('hiringSignContract')({ mode: 'draw', signaturePngBase64: dataUrl });
      }
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to sign contract.');
    } finally {
      setBusy(null);
    }
  };

  const ackTask = async (
    taskId: string,
    completed = true,
    extras?: { attested?: boolean; signedName?: string; signatureMode?: 'draw' | 'type'; signaturePngBase64?: string; pendingReview?: boolean }
  ) => {
    setBusy(taskId);
    setError(null);
    try {
      if (taskId === 'backgroundCheck' && currentUser?.uid) {
        await saveStepProgress(currentUser.uid, ONBOARDING_CERTIFICATION_ID, 'backgroundCheck', {
          status: extras?.pendingReview ? 'pending_review' : completed ? 'completed' : 'not_started',
          completedAt: completed ? new Date().toISOString() : null,
        });
      }
      let onboardingComplete = false;
      try {
        const result: any = await fn('hiringAckOnboardingTask')({
          taskId,
          shirtSize: taskId === 'merch' ? shirtSize : null,
          completed,
          pendingReview: extras?.pendingReview === true,
          attested: extras?.attested,
          signedName: extras?.signedName,
          signatureMode: extras?.signatureMode,
          signaturePngBase64: extras?.signaturePngBase64,
        });
        onboardingComplete = result?.data?.onboardingComplete === true;
      } catch (hiringError: any) {
        if (taskId !== 'backgroundCheck' || extras?.pendingReview !== true) throw hiringError;
      }
      setData((current: any) => ({
        ...current,
        ...(taskId === 'merch' ? { shirtSize } : {}),
        ...(onboardingComplete ? { staffStatus: 'active', hiringStatus: 'onboarding_complete' } : {}),
        tasks: Array.isArray(current?.tasks)
          ? current.tasks.map((task: Task) => task.id === taskId
            ? {
              ...task,
              status: completed ? 'completed' : extras?.pendingReview ? 'pending_review' : 'not_started',
              completedAt: completed ? new Date().toISOString() : null,
              signedName: taskId === 'handbook' && completed ? (extras?.signedName || handbookSignedName.trim()) : null,
              signatureUrl: taskId === 'handbook' && completed && extras?.signaturePngBase64
                ? extras.signaturePngBase64
                : (taskId === 'handbook' && !completed ? null : task.signatureUrl),
            }
            : task)
          : current?.tasks,
      }));
      if (taskId === 'handbook' && !completed) setHandbookAttested(false);
    } catch (e: any) {
      setError(e?.message || 'Failed to update task.');
    } finally {
      setBusy(null);
    }
  };

  const signHandbook = async () => {
    if (!handbookAttested) {
      setError('Confirm that you have read the staff handbook.');
      return;
    }
    if (handbookSignMode === 'type') {
      if (!handbookSignedName.trim()) {
        setError('Type your full name to sign the handbook acknowledgment.');
        return;
      }
      await ackTask('handbook', true, {
        attested: true,
        signedName: handbookSignedName.trim(),
        signatureMode: 'type',
      });
      return;
    }
    const canvas = handbookCanvasRef.current;
    if (!canvas) {
      setError('Signature pad missing.');
      return;
    }
    await ackTask('handbook', true, {
      attested: true,
      signedName: handbookSignedName.trim() || typedName.trim() || String(data?.displayName || userData?.displayName || ''),
      signatureMode: 'draw',
      signaturePngBase64: canvas.toDataURL('image/png'),
    });
  };

  const uploadCert = async (task: Task, file: File) => {
    if (!currentUser?.uid || !task.uploadField) return;
    setBusy(task.id);
    setError(null);
    try {
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const ext = file.name.split('.').pop() || 'pdf';
      const storagePath = `teachers/${currentUser.uid}/compliance/mandatory-reporting/${stamp}.${ext}`;
      const r = storageRef(storage, storagePath);
      await uploadBytes(r, file, { contentType: file.type || 'application/pdf' });
      const url = await getDownloadURL(r);
      const meta = {
        storagePath,
        url,
        filename: file.name,
        contentType: file.type || 'application/pdf',
        sizeBytes: file.size,
        uploadedAt: new Date().toISOString(),
        uploadedBy: currentUser.uid,
      };
      await setDoc(
        doc(db, 'teachers', currentUser.uid),
        {
          [task.uploadField]: meta,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser.uid,
        },
        { merge: true }
      );
      await ackTask(task.id);
    } catch (e: any) {
      setError(e?.message || 'Upload failed.');
      setBusy(null);
    }
  };

  const submitCoriForm = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy('schoolBackgroundCheck');
    setError(null);
    try {
      const result: any = await fn('hiringSubmitStateBackgroundCheckForm')({
        checkType: 'ma-cori',
        form: coriForm,
      });
      setData((current: any) => ({
        ...current,
        ...(result?.data?.onboardingComplete ? { staffStatus: 'active', hiringStatus: 'onboarding_complete' } : {}),
        tasks: Array.isArray(current?.tasks)
          ? current.tasks.map((task: Task) => task.id === 'schoolBackgroundCheck'
            ? { ...task, status: 'completed', completedAt: new Date().toISOString() }
            : task)
          : current?.tasks,
      }));
    } catch (e: any) {
      setError(e?.message || 'Could not submit the CORI authorization form.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading onboarding…</div>;

  const contractSigned = Boolean(data?.contractSignedAt);
  const offerAccepted = Boolean(data?.offerAcceptedAt);
  const configuredSteps = certification?.steps || [];
  const locationContext = String(data?.metro || (userData as any)?.state || '').toLowerCase();
  const isMassachusetts = locationContext.includes('massachusetts')
    || /\bma\b/.test(locationContext)
    || ['boston', 'wayland', 'brookline', 'cambridge', 'arlington', 'swampscott', 'sudbury', 'dover', 'framingham']
      .some(place => locationContext.includes(place));
  const configurationFor = (id: string) =>
    configuredSteps.find(step => (step.onboardingTaskId || step.id) === id);
  const tasks: Task[] = (Array.isArray(data?.tasks) ? data.tasks : [])
    .map((task: Task) => {
      const configured = configurationFor(task.id);
      const legacyGenericBackgroundTitle = task.id === 'backgroundCheck' && configured?.title === 'Background check';
      const legacyGenericSchoolTitle = task.id === 'schoolBackgroundCheck' && configured?.title === 'School-specific background check';
      const legacyGenericDescription = configured?.description === 'Complete the background-check instructions sent to you.'
        || configured?.description === 'Complete this only when required by an assigned school.';
      return configured ? {
        ...task,
        title: legacyGenericBackgroundTitle || legacyGenericSchoolTitle ? task.title : configured.title,
        description: legacyGenericDescription ? task.description : configured.description,
        completion: task.id === 'backgroundCheck' ? 'admin_review' : task.completion,
        linkUrl: task.id === 'handbook' ? STAFF_HANDBOOK_PDF : (configured.linkUrl || task.linkUrl),
        linkLabel: task.id === 'handbook' ? 'Staff handbook PDF' : (configured.linkLabel || task.linkLabel),
        writtenContent: configured.writtenContent,
        video: configured.video,
        documents: configured.documents,
        order: configured.order,
        published: configured.published,
        required: task.id === 'schoolBackgroundCheck' && isMassachusetts
          ? true
          : configured.required,
      } : (task.id === 'backgroundCheck' ? { ...task, completion: 'admin_review' } : task);
    })
    .filter((task: Task) => task.published !== false
      && (task.id !== 'schoolBackgroundCheck' || isMassachusetts))
    .sort((a: Task, b: Task) => (a.order ?? 999) - (b.order ?? 999));
  const offerStep = configurationFor('offer');
  const contractStep = configurationFor('contract');
  const materials = (step?: Partial<CertificationStep | Task>) => {
    if (!step?.writtenContent && !step?.video && !step?.documents?.length) return null;
    return (
      <div className="teacher-onboarding__materials">
        <strong>Learning materials</strong>
        {step.writtenContent && <div className="teacher-onboarding__written">{step.writtenContent}</div>}
        {step.video && <video controls playsInline preload="metadata" src={step.video.url} />}
        {(step.documents || []).map(file => <a key={file.storagePath} href={file.url} target="_blank" rel="noreferrer">{file.name} <span aria-hidden="true">↗</span></a>)}
      </div>
    );
  };
  const checklistTasks = tasks.filter(task => task.id !== 'contract' && ONBOARDING_TASK_IDS.has(task.id));
  const progressTasks = checklistTasks.filter(task => task.required !== false && task.completion !== 'info');
  const completedCount = Number(offerAccepted) + Number(contractSigned)
    + progressTasks.filter(task => task.status === 'completed').length;
  const totalCount = 2 + progressTasks.length;
  const progressPercent = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;
  const dueDate = (() => {
    const raw = data?.onboardingDueAt;
    if (!raw) return null;
    if (typeof raw.toDate === 'function') return raw.toDate();
    const seconds = Number(raw.seconds ?? raw._seconds);
    if (Number.isFinite(seconds) && seconds > 0) return new Date(seconds * 1000);
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  })();
  const dueLabel = dueDate
    ? dueDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : 'See your welcome email';
  const taskUnlocksCertification = (task: Task) =>
    task.status === 'completed'
    || (task.id === 'backgroundCheck' && task.status === 'pending_review');
  const certificationUnlocked = offerAccepted
    && contractSigned
    && progressTasks.every(taskUnlocksCertification);
  const hiringStatus = String((userData as any)?.hiringStatus || data?.hiringStatus || '');
  const platformUnlocked = ((userData?.staffStatus || data?.staffStatus) === 'active')
    && !['offer_sent', 'offer_accepted', 'onboarding'].includes(hiringStatus);
  const canStartCertification = certificationUnlocked || platformUnlocked;

  return (
    <main className="teacher-onboarding">
      <header className="teacher-onboarding__hero">
        <div className="teacher-onboarding__hero-copy">
          <span className="teacher-onboarding__eyebrow">Welcome to the team</span>
          <h1>{certification?.title || 'Teacher onboarding'}</h1>
          <p>{certification?.description || 'Complete your employment, account, compliance, and payroll setup.'}</p>
          <div className="teacher-onboarding__meta">
            <span>{completedCount} of {totalCount} complete</span>
            <span>Onboarding due {dueLabel}</span>
          </div>
        </div>
        <div className="teacher-onboarding__progress" style={{ '--onboarding-progress': `${progressPercent * 3.6}deg` } as React.CSSProperties}>
          <div><strong>{progressPercent}%</strong><span>complete</span></div>
        </div>
      </header>

      <div className="teacher-onboarding__progress-track" aria-label={`${progressPercent}% complete`}>
        <span style={{ width: `${progressPercent}%` }} />
      </div>

      <section className="teacher-onboarding__benefits" aria-labelledby="onboarding-benefits-title">
        <div className="teacher-onboarding__benefits-icon" aria-hidden="true">★</div>
        <div>
          <span className="teacher-onboarding__eyebrow">What you’re working toward</span>
          <h2 id="onboarding-benefits-title">Finish onboarding to officially join the team</h2>
          <p>Completing these steps confirms your employment setup, gives you access to the full Overture platform, makes you eligible for internal opportunities, and unlocks your Overture Teacher Certification path.</p>
        </div>
      </section>

      {error && <div className="teacher-onboarding__alert">{error}</div>}

      {!offerAccepted && (
        <section className="teacher-onboarding__featured teacher-onboarding__featured--offer">
          <div className="teacher-onboarding__step-icon">1</div>
          <div className="teacher-onboarding__step-content">
            <span className="teacher-onboarding__step-kicker">First step</span>
            <h2>{offerStep?.title || 'Review and accept your offer'}</h2>
            <p>{offerStep?.description || 'Review your offer letter, then accept it to continue.'}</p>
            {materials(offerStep || undefined)}
            <div className="teacher-onboarding__actions">
              {data?.offerPdfUrl && <a className="teacher-onboarding__button teacher-onboarding__button--secondary" href={data.offerPdfUrl} target="_blank" rel="noreferrer">View offer letter <span aria-hidden="true">↗</span></a>}
              <button className="teacher-onboarding__button" type="button" disabled={busy === 'offer'} onClick={acceptOwnOffer}>
                {busy === 'offer' ? 'Accepting…' : 'Accept offer & continue'}
              </button>
            </div>
          </div>
        </section>
      )}

      <section className={`teacher-onboarding__featured ${contractSigned ? 'is-complete' : ''}`}>
        <div className="teacher-onboarding__step-icon">{contractSigned ? '✓' : '2'}</div>
        <div className="teacher-onboarding__step-content">
          <div className="teacher-onboarding__title-row">
            <div>
              <span className="teacher-onboarding__step-kicker">{contractSigned ? 'Completed' : 'Employment paperwork'}</span>
              <h2>{contractStep?.title || 'Employment contract'}</h2>
            </div>
            {contractSigned && <span className="teacher-onboarding__status is-done">Complete</span>}
          </div>
          <p>{contractStep?.description || 'Download the agreement, then sign it here.'}</p>
          {materials(contractStep || undefined)}

          {contractSigned ? (
            <div className="teacher-onboarding__completed-actions">
              <span><span aria-hidden="true">✓</span> Your signed contract is on file</span>
              <div className="teacher-onboarding__actions">
                {data?.contractSignedPdfUrl && <a className="teacher-onboarding__button teacher-onboarding__button--secondary" href={data.contractSignedPdfUrl} target="_blank" rel="noreferrer">View signed PDF</a>}
              </div>
            </div>
          ) : offerAccepted ? (
            <div className="teacher-onboarding__signature">
              {data?.contractUnsignedPdfUrl && <a className="teacher-onboarding__download" href={data.contractUnsignedPdfUrl} target="_blank" rel="noreferrer"><span aria-hidden="true">↓</span><span><strong>Employment agreement</strong><small>Download unsigned PDF</small></span></a>}
              <div className="teacher-onboarding__signature-tabs" role="tablist" aria-label="Signature method">
                {(['draw', 'type', 'upload'] as const).map(mode => (
                  <button key={mode} type="button" role="tab" aria-selected={signMode === mode} className={signMode === mode ? 'is-active' : ''} onClick={() => setSignMode(mode)}>
                    {mode === 'draw' ? 'Draw signature' : mode === 'type' ? 'Type my name' : 'Upload signed PDF'}
                  </button>
                ))}
              </div>
              {signMode === 'draw' && <>
                <p className="teacher-onboarding__field-label">Sign inside the box</p>
                <canvas ref={canvasRef} width={640} height={180} onPointerDown={event => pointer(canvasRef.current, event, 'start')} onPointerMove={event => pointer(canvasRef.current, event, 'move')} onPointerUp={event => pointer(canvasRef.current, event, 'end')} onPointerLeave={event => pointer(canvasRef.current, event, 'end')} />
                <div className="teacher-onboarding__actions">
                  <button className="teacher-onboarding__button teacher-onboarding__button--quiet" type="button" onClick={() => clearCanvas(canvasRef.current)}>Clear</button>
                  <button className="teacher-onboarding__button" type="button" disabled={busy === 'contract'} onClick={() => signContract()}>{busy === 'contract' ? 'Submitting…' : 'Sign & submit'}</button>
                </div>
              </>}
              {signMode === 'type' && <div className="teacher-onboarding__typed-signature">
                <label>Full legal name<input value={typedName} onChange={event => setTypedName(event.target.value)} placeholder="Type your full legal name" /></label>
                <button className="teacher-onboarding__button" type="button" disabled={busy === 'contract' || !typedName.trim()} onClick={() => signContract()}>{busy === 'contract' ? 'Submitting…' : 'Sign with typed name'}</button>
              </div>}
              {signMode === 'upload' && <label className="teacher-onboarding__file-drop">Choose a signed PDF or image<input type="file" accept="application/pdf,image/*" onChange={event => { const file = event.target.files?.[0]; if (file) signContract(file); }} /></label>}
            </div>
          ) : <div className="teacher-onboarding__locked-note"><span aria-hidden="true">🔒</span> Accept your offer to unlock the employment agreement.</div>}
        </div>
      </section>

      <section className="teacher-onboarding__checklist">
        <div className="teacher-onboarding__section-heading">
          <div><span className="teacher-onboarding__eyebrow">Your checklist</span><h2>Finish setting up</h2></div>
          <span>{progressTasks.filter(task => task.status === 'completed').length}/{progressTasks.length}</span>
        </div>
        {!contractSigned && <div className="teacher-onboarding__locked-banner">Complete your employment contract to unlock the remaining tasks.</div>}
        <div className="teacher-onboarding__task-list">
          {checklistTasks.map((task, index) => {
            const locked = task.requiresContract && !contractSigned;
            const done = task.status === 'completed';
            const pendingReview = task.status === 'pending_review';
            const status = locked ? 'Locked' : done ? 'Complete' : pendingReview ? 'Awaiting review' : task.completion === 'info' ? 'As needed' : task.completion === 'admin' ? 'Admin review' : 'To do';
            return (
              <article key={task.id} id={`task-${task.id}`} className={`teacher-onboarding__task ${done ? 'is-complete' : ''} ${locked ? 'is-locked' : ''}`}>
                <div className="teacher-onboarding__task-marker">{done ? '✓' : index + 3}</div>
                <div className="teacher-onboarding__task-body">
                  <div className="teacher-onboarding__title-row">
                    <div><h3>{task.title}</h3><p>{task.description}</p></div>
                    <span className={`teacher-onboarding__status ${done ? 'is-done' : ''} ${pendingReview ? 'is-pending' : ''}`}>{status}</span>
                  </div>
                  {materials(task)}
                  {!locked && !done && <div className="teacher-onboarding__task-actions">
                    {task.linkUrl && task.id !== 'handbook' && <a className="teacher-onboarding__button teacher-onboarding__button--secondary" href={task.linkUrl} target="_blank" rel="noreferrer">{task.linkLabel || 'Open resource'} <span aria-hidden="true">↗</span></a>}
                    {task.id === 'profile' && <Link className="teacher-onboarding__button teacher-onboarding__button--secondary" to="/app/profile">Open your profile</Link>}
                    {task.id === 'merch' && <div className="teacher-onboarding__inline-field"><input value={shirtSize} onChange={event => setShirtSize(event.target.value)} placeholder="Shirt size (e.g. M)" /><button className="teacher-onboarding__button" type="button" disabled={busy === task.id || !shirtSize.trim()} onClick={() => ackTask(task.id)}>Save & complete</button></div>}
                    {task.completion === 'state_background_form' && (
                      <form className="teacher-onboarding__background-form" onSubmit={submitCoriForm}>
                        <p className="teacher-onboarding__form-note">Your information is confidential and will only be used to request your CORI. Required fields are marked with *.</p>
                        <div className="teacher-onboarding__form-grid">
                          <label><span>First name *</span><input required value={coriForm.firstName} onChange={event => setCoriForm({ ...coriForm, firstName: event.target.value })} /></label>
                          <label><span>Middle initial</span><input value={coriForm.middleInitial} onChange={event => setCoriForm({ ...coriForm, middleInitial: event.target.value })} /></label>
                          <label><span>Last name *</span><input required value={coriForm.lastName} onChange={event => setCoriForm({ ...coriForm, lastName: event.target.value })} /></label>
                          <label><span>Suffix</span><input value={coriForm.suffix} onChange={event => setCoriForm({ ...coriForm, suffix: event.target.value })} /></label>
                          <label className="is-wide"><span>Former last names</span><input value={coriForm.formerLastNames[0] || ''} onChange={event => setCoriForm({ ...coriForm, formerLastNames: [event.target.value] })} /></label>
                          <label><span>Date of birth *</span><input required type="date" value={coriForm.dateOfBirth} onChange={event => setCoriForm({ ...coriForm, dateOfBirth: event.target.value })} /></label>
                          <label><span>Place of birth</span><input value={coriForm.placeOfBirth} onChange={event => setCoriForm({ ...coriForm, placeOfBirth: event.target.value })} /></label>
                          <label className="is-wide"><span>Last six digits of SSN *</span><input required={!coriForm.noSocialSecurityNumber} inputMode="numeric" pattern="[0-9]{6}" maxLength={6} disabled={coriForm.noSocialSecurityNumber} value={coriForm.lastSixSsn} onChange={event => setCoriForm({ ...coriForm, lastSixSsn: event.target.value.replace(/\D/g, '').slice(0, 6) })} /></label>
                          <label className="is-wide teacher-onboarding__check"><input type="checkbox" checked={coriForm.noSocialSecurityNumber} onChange={event => setCoriForm({ ...coriForm, noSocialSecurityNumber: event.target.checked, lastSixSsn: event.target.checked ? '' : coriForm.lastSixSsn })} /><span>I do not have a Social Security number</span></label>
                          <label><span>Sex</span><input value={coriForm.sex} onChange={event => setCoriForm({ ...coriForm, sex: event.target.value })} /></label>
                          <label><span>Height</span><input value={coriForm.height} onChange={event => setCoriForm({ ...coriForm, height: event.target.value })} /></label>
                          <label><span>Eye color</span><input value={coriForm.eyeColor} onChange={event => setCoriForm({ ...coriForm, eyeColor: event.target.value })} /></label>
                          <label><span>Race</span><input value={coriForm.race} onChange={event => setCoriForm({ ...coriForm, race: event.target.value })} /></label>
                          <label><span>Driver’s license or ID number</span><input value={coriForm.governmentIdNumber} onChange={event => setCoriForm({ ...coriForm, governmentIdNumber: event.target.value })} /></label>
                          <label><span>State of issue</span><input value={coriForm.governmentIdState} onChange={event => setCoriForm({ ...coriForm, governmentIdState: event.target.value })} /></label>
                          <label className="is-wide"><span>Father’s full name</span><input value={coriForm.fatherFullName} onChange={event => setCoriForm({ ...coriForm, fatherFullName: event.target.value })} /></label>
                          <label className="is-wide"><span>Mother’s full name</span><input value={coriForm.motherFullName} onChange={event => setCoriForm({ ...coriForm, motherFullName: event.target.value })} /></label>
                          <label className="is-wide"><span>Street address *</span><input required value={coriForm.streetAddress} onChange={event => setCoriForm({ ...coriForm, streetAddress: event.target.value })} /></label>
                          <label><span>Apartment or suite</span><input value={coriForm.apartment} onChange={event => setCoriForm({ ...coriForm, apartment: event.target.value })} /></label>
                          <label><span>City *</span><input required value={coriForm.city} onChange={event => setCoriForm({ ...coriForm, city: event.target.value })} /></label>
                          <label><span>State *</span><input required value={coriForm.state} onChange={event => setCoriForm({ ...coriForm, state: event.target.value })} /></label>
                          <label><span>ZIP *</span><input required value={coriForm.zip} onChange={event => setCoriForm({ ...coriForm, zip: event.target.value })} /></label>
                          <label><span>Preferred email</span><input type="email" value={coriForm.preferredEmail} onChange={event => setCoriForm({ ...coriForm, preferredEmail: event.target.value })} /></label>
                          <label><span>Phone</span><input type="tel" value={coriForm.phone} onChange={event => setCoriForm({ ...coriForm, phone: event.target.value })} /></label>
                        </div>
                        <div className="teacher-onboarding__authorization">
                          <p>I understand that Overture Games, Inc. (d/b/a Overture) will submit a CORI check using my personal information. I acknowledge and give Overture permission to submit that check to the Massachusetts Department of Criminal Justice Information Services. This authorization is valid for one year from the date I sign it, and Overture may conduct subsequent CORI checks during that year. I may withdraw this authorization at any time by giving Overture written notice. By signing, I affirm that the information I provided is true and accurate.</p>
                          <label><span>Type your full legal name to sign *</span><input required value={coriForm.signedName} onChange={event => setCoriForm({ ...coriForm, signedName: event.target.value })} /></label>
                          <label className="teacher-onboarding__check"><input required type="checkbox" checked={coriForm.attested} onChange={event => setCoriForm({ ...coriForm, attested: event.target.checked })} /><span>I authorize Overture to run this background check.</span></label>
                        </div>
                        <button className="teacher-onboarding__button" type="submit" disabled={busy === task.id}>{busy === task.id ? 'Submitting securely…' : 'Sign and submit form'}</button>
                      </form>
                    )}
                    {task.id === 'handbook' && (
                      <div className="teacher-onboarding__handbook-sign">
                        <a className="teacher-onboarding__download" href={STAFF_HANDBOOK_PDF} target="_blank" rel="noreferrer">
                          <span aria-hidden="true">↓</span>
                          <span><strong>Staff handbook</strong><small>Download and review the 2026 PDF</small></span>
                        </a>
                        <label className="teacher-onboarding__attestation">
                          <input type="checkbox" checked={handbookAttested} onChange={event => setHandbookAttested(event.target.checked)} />
                          <span>I have read the Overture Staff Handbook and agree to follow its policies.</span>
                        </label>
                        <div className="teacher-onboarding__signature-tabs" role="tablist" aria-label="Handbook signature method">
                          {(['draw', 'type'] as const).map(mode => (
                            <button key={mode} type="button" role="tab" aria-selected={handbookSignMode === mode} className={handbookSignMode === mode ? 'is-active' : ''} onClick={() => setHandbookSignMode(mode)}>
                              {mode === 'draw' ? 'Draw signature' : 'Type my name'}
                            </button>
                          ))}
                        </div>
                        {handbookSignMode === 'draw' && <>
                          <p className="teacher-onboarding__field-label">Sign inside the box</p>
                          <canvas
                            ref={handbookCanvasRef}
                            width={640}
                            height={180}
                            onPointerDown={event => pointer(handbookCanvasRef.current, event, 'start')}
                            onPointerMove={event => pointer(handbookCanvasRef.current, event, 'move')}
                            onPointerUp={event => pointer(handbookCanvasRef.current, event, 'end')}
                            onPointerLeave={event => pointer(handbookCanvasRef.current, event, 'end')}
                          />
                          <div className="teacher-onboarding__actions">
                            <button className="teacher-onboarding__button teacher-onboarding__button--quiet" type="button" onClick={() => clearCanvas(handbookCanvasRef.current)}>Clear</button>
                            <button className="teacher-onboarding__button" type="button" disabled={busy === task.id || !handbookAttested} onClick={() => void signHandbook()}>
                              {busy === task.id ? 'Submitting…' : 'Sign & submit'}
                            </button>
                          </div>
                        </>}
                        {handbookSignMode === 'type' && <div className="teacher-onboarding__typed-signature">
                          <label>Full legal name<input value={handbookSignedName} onChange={event => setHandbookSignedName(event.target.value)} placeholder="Type your full legal name" /></label>
                          <button className="teacher-onboarding__button" type="button" disabled={busy === task.id || !handbookAttested || !handbookSignedName.trim()} onClick={() => void signHandbook()}>
                            {busy === task.id ? 'Submitting…' : 'Sign with typed name'}
                          </button>
                        </div>}
                      </div>
                    )}
                    {task.completion === 'teacher_ack' && task.id !== 'merch' && task.id !== 'handbook' && <button className="teacher-onboarding__button" type="button" disabled={busy === task.id} onClick={() => ackTask(task.id)}>{busy === task.id ? 'Saving…' : 'Mark complete'}</button>}
                    {task.completion === 'admin_review' && task.status !== 'pending_review' && (
                      <button className="teacher-onboarding__button" type="button" disabled={busy === task.id} onClick={() => ackTask(task.id, false, { pendingReview: true })}>
                        {busy === task.id ? 'Submitting…' : 'Request review'}
                      </button>
                    )}
                    {task.completion === 'admin_review' && task.status === 'pending_review' && (
                      <>
                        <span className="teacher-onboarding__signed-note">Submitted for admin review</span>
                        <button className="teacher-onboarding__button teacher-onboarding__button--quiet" type="button" disabled={busy === task.id} onClick={() => ackTask(task.id, false)}>
                          {busy === task.id ? 'Updating…' : 'Cancel request'}
                        </button>
                      </>
                    )}
                    {task.completion === 'upload' && <label className="teacher-onboarding__button">Upload certificate<input hidden type="file" accept="application/pdf,image/*" onChange={event => { const file = event.target.files?.[0]; if (file) uploadCert(task, file); }} /></label>}
                  </div>}
                  {!locked && done && <div className="teacher-onboarding__task-actions">
                    {(task.id === 'handbook' || task.linkUrl) && (
                      <a className="teacher-onboarding__button teacher-onboarding__button--secondary" href={task.id === 'handbook' ? STAFF_HANDBOOK_PDF : task.linkUrl!} target="_blank" rel="noreferrer">
                        {task.id === 'handbook' ? 'Review handbook PDF ↗' : 'Review resource ↗'}
                      </a>
                    )}
                    {task.id === 'merch' && <div className="teacher-onboarding__inline-field"><input value={shirtSize} onChange={event => setShirtSize(event.target.value)} placeholder="Shirt size (e.g. M)" /><button className="teacher-onboarding__button teacher-onboarding__button--secondary" type="button" disabled={busy === task.id || !shirtSize.trim()} onClick={() => ackTask(task.id)}>Update shirt size</button></div>}
                    {task.id === 'handbook' && task.signedName && <span className="teacher-onboarding__signed-note">Signed by {task.signedName}</span>}
                    {task.id === 'handbook' && task.signatureUrl && <a className="teacher-onboarding__button teacher-onboarding__button--secondary" href={task.signatureUrl} target="_blank" rel="noreferrer">View signature</a>}
                    {!['admin', 'admin_review', 'info'].includes(task.completion) && <button className="teacher-onboarding__button teacher-onboarding__button--quiet" type="button" disabled={busy === task.id} onClick={() => ackTask(task.id, false)}>{busy === task.id ? 'Updating…' : 'Mark incomplete'}</button>}
                  </div>}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <aside className={`teacher-onboarding__next-step ${canStartCertification ? 'is-ready' : ''}`}>
        <div>
          <span className="teacher-onboarding__eyebrow">Your next step</span>
          <h2>Overture Teacher Certification</h2>
          <p>
            {canStartCertification
              ? 'Onboarding is far enough along to start teacher certification. Checkr can finish in the background while you complete training.'
              : 'Finish the required onboarding items. After you submit Checkr for review, you can start teacher certification while Overture confirms the result.'}
          </p>
          {canStartCertification
            ? <Link className="teacher-onboarding__button teacher-onboarding__completed-link" to={`/app/resources/${TEACHER_CERTIFICATION_ID}`}>Start teacher certification</Link>
            : <span className="teacher-onboarding__next-locked">Submit Checkr for review, then this unlocks.</span>}
        </div>
        <div className="teacher-onboarding__next-benefits" aria-label="Teacher certification benefits">
          <span><b aria-hidden="true">✓</b> Qualify to lead Overture classes</span>
          <span><b aria-hidden="true">✓</b> Become eligible to substitute for classes</span>
          <span><b aria-hidden="true">✓</b> Unlock the certified teacher rate of $70 per class</span>
          <span><b aria-hidden="true">✓</b> Build confidence with Overture’s teaching model</span>
        </div>
      </aside>
    </main>
  );
}
